/**
 * Palette de couleurs des blocs de programme (planificateur pro + portail client).
 * L'index choisi par le praticien est persisté dans `groupDescription` sous la
 * forme d'une méta `@@color:N@@` (préfixe), optionnellement `@@client:id@@`,
 * suivie du texte d'instruction libre.
 */
export const BLOCK_COLOR_DOTS = [
  "#2C2C2A",
  "#D85A30",
  "#EF9F27",
  "#FAEEDA",
  "#1D9E75",
  "#378ADD",
  "#7F77DD",
] as const;

const GROUP_COLOR_META_RE = /^@@color:(\d+)@@/;
const GROUP_CLIENT_META_RE = /@@client:([^@]+)@@/;

export type BlockColorSurfaceStyle = {
  backgroundColor: string;
  borderLeft: string;
  color: string;
  mutedColor: string;
  /** Vrai si la teinte du bloc est claire (texte sombre recommandé). */
  isLight: boolean;
};

const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace("#", "");
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Luminance relative WCAG (sRGB). */
const relativeLuminance = (hex: string): number => {
  const normalized = hex.replace("#", "");
  const channels = [0, 2, 4].map((offset) => {
    const channel = Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return (
    0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!
  );
};

const stripPlannerMeta = (description?: string | null): string | null => {
  if (!description) return null;
  const stripped = description
    .replace(GROUP_COLOR_META_RE, "")
    .replace(GROUP_CLIENT_META_RE, "")
    .trim();
  return stripped || null;
};

/** Construit une `groupDescription` encodant couleur, client optionnel et instruction. */
export const encodeGroupColorMeta = (
  colorIndex: number,
  description?: string | null,
  clientId?: string | null,
): string => {
  const rest = stripPlannerMeta(description) ?? "";
  const clientPart = clientId?.trim() ? `@@client:${clientId.trim()}@@` : "";
  return `@@color:${colorIndex}@@${clientPart}${rest}`;
};

/** Extrait l'index couleur depuis une `groupDescription`, ou null. */
export const parseGroupColorIndex = (
  description?: string | null,
): number | null => {
  const match = description?.match(GROUP_COLOR_META_RE);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  return value;
};

/** Extrait l'id client assigné au bloc depuis une `groupDescription`, ou null. */
export const parseGroupClientId = (
  description?: string | null,
): string | null => {
  const match = description?.match(GROUP_CLIENT_META_RE);
  return match?.[1]?.trim() || null;
};

/** Retire les métas planificateur et renvoie le texte d'instruction (ou null). */
export const stripGroupColorMeta = (
  description?: string | null,
): string | null => stripPlannerMeta(description);

/** Normalise un index couleur (modulo palette, jamais négatif). */
export const resolveBlockColorIndex = (
  colorIndex: number | null | undefined,
  fallbackIndex = 0,
): number => {
  const idx = colorIndex != null ? colorIndex : fallbackIndex;
  const len = BLOCK_COLOR_DOTS.length;
  return ((idx % len) + len) % len;
};

/** Couleur hex pleine pour un index donné. */
export const blockColorHex = (
  colorIndex: number | null | undefined,
  fallbackIndex = 0,
): string => BLOCK_COLOR_DOTS[resolveBlockColorIndex(colorIndex, fallbackIndex)]!;

/** Style de surface (fond, bordure, texte) avec contraste lisible sur la teinte. */
export const blockColorSurface = (
  colorIndex: number | null | undefined,
  fallbackIndex = 0,
): BlockColorSurfaceStyle => {
  const color = blockColorHex(colorIndex, fallbackIndex);
  const light = relativeLuminance(color) > 0.52;
  return {
    backgroundColor: hexToRgba(color, light ? 0.22 : 0.9),
    borderLeft: `3px solid ${color}`,
    color: light ? "#1c1917" : "#fafaf9",
    mutedColor: light ? "rgba(28, 25, 23, 0.68)" : "rgba(250, 250, 249, 0.78)",
    isLight: light,
  };
};
